import React, { useState } from 'react';
import ArrowRightIcon from '@liquidweb-dev/undercurrent/icons/lucide/ArrowRight';
import ChevronIcon from '@liquidweb-dev/undercurrent/icons/animated/Chevron';
import ListSubheader from '@liquidweb-dev/undercurrent/ListSubheader';
import MenuItem from '@liquidweb-dev/undercurrent/MenuItem';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import Collapse from '@liquidweb-dev/undercurrent/Collapse';
import useAssetCancellationReasonList from 'modules/queries/asset/cancellation/useReasonList';
import useReportRelatedTerminationActions from 'modules/queries/asset/useReportRelatedTerminationActions';
import LWForm, { type FormSubmitHandler } from 'components/LWForm';
import HookedTextField from 'components/standard/Form/HookedTextField';
import HookedSubmit from 'components/standard/Form/HookedSubmit';
import Addon from './Addon';
import { type CancellationFormData } from './constants';

export interface CancelServiceProps {
	uniqId: string;
	formValues: CancellationFormData;
	onFormValuesChange: (values: CancellationFormData) => void;
	onSubmit: FormSubmitHandler<CancellationFormData>;
}

const CancelService = ({
	uniqId,
	formValues,
	onFormValuesChange,
	onSubmit,
}: CancelServiceProps) => {
	const { isLoading: isReasonsLoading, data: cancellationReasons } =
		useAssetCancellationReasonList();
	const { data: terminationActions } = useReportRelatedTerminationActions(
		{ uniq_id: uniqId },
		{ enabled: Boolean(uniqId) },
	);

	const [formErrors, setFormErrors] = useState({
		cancellation_reason: '',
	});
	const [isMoreToSayOpen, setMoreToSayOpen] = useState(false);

	const handleMoreToSayClick = () => {
		setMoreToSayOpen(!isMoreToSayOpen);
	};

	return (
		<LWForm
			values={formValues}
			onValuesChange={onFormValuesChange}
			errors={formErrors}
			onErrorsChange={setFormErrors}
			validators={{
				cancellation_reason: (value) => {
					if (value === '') return 'Please select a cancellation reason';
					return '';
				},
			}}
			onSubmit={onSubmit}
		>
			<Stack width="100%" direction="column" gap={400} useFlexGap>
				<Stack direction="column" gap={100} useFlexGap>
					<Typography fontWeight="500" variant="title-md">
						Canceling your service? Tell us a bit more
					</Typography>
					<Typography variant="body-md">
						We want to make sure your cancellation is handled quickly and
						correctly. Let us know why you’re canceling so we can provide the
						right options, whether it’s a smooth decommissioning, assistance
						with migration, or answering any final questions.
					</Typography>
				</Stack>
				<Stack direction="column" gap={150} useFlexGap>
					<HookedTextField
						name="cancellation_reason"
						label="Reason for cancellation"
						size="large"
						fullWidth
						select
						required
						disabled={isReasonsLoading}
						slotProps={{
							select: {
								displayEmpty: true,
							},
						}}
					>
						<MenuItem value="" disabled sx={{ display: 'none' }}>
							Select a reason
						</MenuItem>
						{cancellationReasons?.map((data) => {
							if (data.type === 'category') {
								return (
									<ListSubheader
										sx={{
											backgroundColor: 'transparent',
											paddingX: 200,
											paddingY: 50,
										}}
										key={data.label}
									>
										<Typography fontWeight={500} variant="label-xl">
											{data.label}
										</Typography>
									</ListSubheader>
								);
							}

							return (
								<MenuItem key={data.label} value={data.label}>
									<Typography
										fontWeight={400}
										variant="label-xl"
										lineHeight="24px"
										whiteSpace="normal"
									>
										{data.label}
									</Typography>
								</MenuItem>
							);
						})}
					</HookedTextField>
				</Stack>
				{terminationActions && terminationActions?.assets.length > 0 && (
					<Stack
						padding={300}
						gap={200}
						useFlexGap
						sx={(theme) => ({
							borderRadius: theme.borderRadius.rounded,
							border: `1px solid ${theme.vars.palette.uc.danger.border.weak}`,
						})}
					>
						<Stack
							direction="column"
							alignItems="flex-start"
							justifyContent="flex-start"
							gap={50}
							useFlexGap
						>
							<Typography fontWeight={500} variant="body-md">
								Add-ons cancelled with server
							</Typography>
							<Typography variant="body-sm">
								There are some add-ons that you&apos;ll lose access to if you
								cancel this server
							</Typography>
						</Stack>
						<Stack
							minWidth="300px"
							alignContent="flex-start"
							flexWrap="wrap"
							direction="row"
							rowGap={150}
							columnGap={300}
						>
							{terminationActions?.assets.map((action) => (
								<Addon key={action.uniq_id} data={action} />
							))}
						</Stack>
					</Stack>
				)}
				<Stack>
					<Stack
						direction="row"
						justifyContent="space-between"
						sx={{ paddingY: 100, cursor: 'pointer' }}
						onClick={handleMoreToSayClick}
					>
						<Typography variant="body-sm">Have more to say?</Typography>
						<ChevronIcon
							open={isMoreToSayOpen}
							sx={{
								height: ({ size }) => size.sm,
								width: ({ size }) => size.sm,
							}}
						/>
					</Stack>
					<Collapse in={isMoreToSayOpen}>
						<HookedTextField
							name="additional_info"
							multiline
							placeholder="Anything else you'd like to let us know?"
							minRows={4}
							size="medium"
							fullWidth
							sx={{ marginTop: 200 }}
						/>
					</Collapse>
				</Stack>
				<Stack alignItems="center">
					<HookedSubmit
						size="large"
						variant="primary"
						endIcon={<ArrowRightIcon />}
						sx={{ width: 200 }}
					>
						Next
					</HookedSubmit>
				</Stack>
			</Stack>
		</LWForm>
	);
};

export default CancelService;
